import { fetcher } from "@certn/api";

import {
  documentDownloadUrlPath,
  DOCUMENT_CONTENT_TYPES,
  itemDocumentsPath,
  itemUploadUrlPath,
  MAX_DOCUMENT_SIZE_BYTES,
  MAX_UPLOAD_FILENAME_LENGTH,
} from "./constants";
import {
  DownloadUrlResponse,
  InformationRequestDocument,
  UploadUrlResponse,
} from "./types";

const UNSAFE_FILENAME_CHARS = /[^A-Za-z0-9 _.-]+/g;

export type UploadProblem = "unsupported" | "too-big";

/** The backend only accepts letters, numbers, spaces, dots, dashes and underscores. */
export function toUploadFilename(name: string) {
  const lastDot = name.lastIndexOf(".");
  const hasExtension = lastDot >= 0;
  const extension = hasExtension
    ? name
        .slice(lastDot)
        .replace(UNSAFE_FILENAME_CHARS, "")
        .slice(0, MAX_UPLOAD_FILENAME_LENGTH - 1)
    : "";
  const stem =
    (hasExtension ? name.slice(0, lastDot) : name)
      .replace(UNSAFE_FILENAME_CHARS, "_")
      .replace(/\.{2,}/g, ".")
      .replace(/^[.\s]+/, "") || "document";
  const shortStem = stem
    .slice(0, MAX_UPLOAD_FILENAME_LENGTH - extension.length)
    .replace(/\.+$/, "");
  return `${shortStem}${extension}`;
}

export function getUploadProblem(file: File): UploadProblem | undefined {
  if (!DOCUMENT_CONTENT_TYPES.includes(file.type)) {
    return "unsupported";
  }
  if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
    return "too-big";
  }
  return undefined;
}

export async function uploadItemDocument(
  caseId: string,
  itemId: string,
  file: File
) {
  const { url, key, tags } = await fetcher.submit<UploadUrlResponse>(
    itemUploadUrlPath(caseId, itemId),
    { filename: toUploadFilename(file.name), content_type: file.type }
  );
  const headers = new Headers({ "Content-Type": file.type });
  if (tags) {
    headers.append("x-amz-tagging", tags);
  }
  const upload = await fetch(url, { method: "PUT", body: file, headers });
  if (!upload.ok) {
    throw new Error(`Upload to storage failed with status ${upload.status}`);
  }
  return fetcher.submit<InformationRequestDocument>(
    itemDocumentsPath(caseId, itemId),
    { key }
  );
}

/** Opens the tab straight away so browsers treat it as part of the click. */
export async function openDocument(caseId: string, documentId: string) {
  const tab = window.open("", "_blank");
  try {
    const { presigned_url } = await fetcher.request<DownloadUrlResponse>(
      documentDownloadUrlPath(caseId, documentId)
    );
    if (tab) {
      tab.opener = null;
      tab.location.href = presigned_url;
    } else {
      window.open(presigned_url, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    tab?.close();
    throw error;
  }
}
